import { useTranslation } from "react-i18next";
import { FieldErrors } from "react-hook-form";
import { InLineAlert } from "@ucl/dse/inLineAlert";
import { Mode, ThemeProvider } from "@ucl/context";
import { creditThemeSet } from "@ucl/styles/themes/designSystem/credit/themeSet";
import { parseFormErrors } from "src/utils/form-error-parser";

interface UseErrorSummaryProps {
    errors: FieldErrors;
    submitError: string | null | undefined;
}

/**
 * returns a renderable InLineAlert component listing all unique errors, or null if there are no errors.
 */
export const useErrorSummary = ({ errors, submitError }: UseErrorSummaryProps) => {
    const { t } = useTranslation();

    let title: string = "";
    let message: string = "";

    const hasFieldErrors = Object.keys(errors).length > 0;
    const hasSubmitError = Boolean(submitError);

    if (!hasFieldErrors && !hasSubmitError) {
        return null;
    }

    if (hasSubmitError) {
        title = t("personalDetails.errorSectionSubmitHeading");
        message = t("personalDetails.errorSectionBody.submit");
    } else {
        title = t("personalDetails.errorSectionFieldHeading");

        const parsedErrors = parseFormErrors(errors);
        const fieldNames = parsedErrors.map((error) => error.fieldName);

        const uniqueFieldNames = Array.from(
            new Set(
                fieldNames.map((fieldName) => {
                    if (fieldName.startsWith("current address ")) {
                        return "current address";
                    } else if (fieldName.startsWith("previous address ")) {
                        return "previous address";
                    }
                    return fieldName;
                })
            )
        );

        const translatedLabels = uniqueFieldNames.map((fieldName) => {
            if (fieldName === "current address") {
                return t("addressDetails.currentAddressDetails");
            } else if (fieldName === "previous address") {
                return t("addressDetails.previousAddressDetails");
            } else if (fieldName === "date of birth") {
                return t("personalDetails.labels.dobCombined").toLowerCase();
            } else {
                return t(`personalDetails.labels.${fieldName}`).toLowerCase();
            }
        });

        message = t("personalDetails.errorSectionBody.field", {
            errorFields: translatedLabels.join(", "),
            interpolation: { escapeValue: false },
        });
    }

    return (
        <ThemeProvider
            themeSet={creditThemeSet}
            mode={Mode.LIGHT_MODE}
            productTeamName="Pre Qualification"
            disableAnalytics
        >
            <InLineAlert
                aria-live="polite"
                variant={"error"}
                data-testid="errorMessage"
                titleProp={{
                    headingLevel: "h2",
                    title,
                }}
                body={message}
            />
        </ThemeProvider>
    );
};
